import { z } from 'zod';
import { NonEmptyTrimmedSchema, UuidSchema } from './common.js';

export const SignupRequestSchema = z.object({
  email: z.string().email().toLowerCase(),
  password: z.string().min(10).max(128),
  display_name: NonEmptyTrimmedSchema.max(80),
  tenant_name: NonEmptyTrimmedSchema.max(80),
});
export type SignupRequest = z.infer<typeof SignupRequestSchema>;

export const LoginRequestSchema = z.object({
  email: z.string().email().toLowerCase(),
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const AuthTokensSchema = z.object({
  access_token: z.string(),
  expires_in: z.number().int(),
  token_type: z.literal('Bearer'),
});
export type AuthTokens = z.infer<typeof AuthTokensSchema>;

export const MeResponseSchema = z.object({
  user: z.object({
    id: UuidSchema,
    email: z.string().email(),
    display_name: z.string().nullable(),
  }),
  tenant: z.object({
    id: UuidSchema,
    name: z.string(),
    slug: z.string(),
    plan: z.string(),
    /**
     * Per-tenant feature-flag map. Populated by /v1/me + signup + PATCH /v1/me
     * from FeatureFlagsRepo.listForTenant. Frontend `useFeatureFlag` reads
     * from here via the Zustand auth store; missing entries default to false.
     */
    feature_flags: z.record(z.string(), z.boolean()).default({}),
  }),
  role: z.string(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/**
 * PATCH /v1/me — partial profile update. Fields are optional so the client
 * can send only what changed. Email isn't editable here yet (no verification
 * flow); add it once we have a confirm-via-email path.
 */
export const UpdateProfileRequestSchema = z.object({
  display_name: NonEmptyTrimmedSchema.max(80).optional(),
});
export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequestSchema>;

/**
 * POST /v1/me/password — change password. Requires the current password as a
 * second factor so a stolen access token can't silently take over the
 * account.
 */
export const ChangePasswordRequestSchema = z
  .object({
    current_password: z.string().min(1, 'Current password is required'),
    new_password: z
      .string()
      .min(10, 'At least 10 characters')
      .max(128, 'At most 128 characters'),
  })
  .refine((v) => v.current_password !== v.new_password, {
    path: ['new_password'],
    message: 'New password must differ from current password',
  });
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;
